import { Link } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { parseAsStringLiteral, useQueryStates } from 'nuqs'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { StatusPill } from '@/components/status-pill'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { formatMoney, resultCount } from '@/lib/utils'
import { useOwedOverview } from './api'
import { groupByParty, matchesSearch, nextStepText, owedStatusPill } from './model'
import type { OwedOverviewItem } from './types'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'

const TEAMS = ['all', 'ops', 'accounting', 'sales'] as const
const TEAM_LABELS: Record<(typeof TEAMS)[number], string> = {
  all: 'All teams',
  ops: 'Ops',
  accounting: 'Accounting',
  sales: 'Sales',
}

const LINK_CLASS =
  'text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground'

function OverviewRow({ item }: { item: OwedOverviewItem }) {
  const pill = owedStatusPill(item)
  const next = nextStepText(item)
  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <p className="min-w-0 flex-1 basis-48 break-words text-sm font-medium leading-snug">
          {item.purchase_order ? (
            <Link to="/purchase-orders/$poId" params={{ poId: item.purchase_order.uuid }} className={LINK_CLASS}>
              {item.summary}
            </Link>
          ) : (
            item.summary
          )}
          {item.amount != null && (
            <span className="ml-1.5 font-medium tabular-nums text-muted-foreground">{formatMoney(item.amount)}</span>
          )}
        </p>
        <StatusPill tone={pill.tone} className="shrink-0 tabular-nums">
          {pill.text}
        </StatusPill>
      </div>
      {next && <p className="mt-0.5 break-words text-xs text-muted-foreground">{next}</p>}
    </li>
  )
}

/** Everything suppliers, carriers and customers still owe us, across all orders (USA-1444). */
export function WaitingOnPage() {
  const [search, setSearch] = useUrlSearchText()
  const [{ team }, setFilters] = useQueryStates({ team: parseAsStringLiteral(TEAMS).withDefault('all') })
  const overview = useOwedOverview(team)
  const matching = (overview.data ?? []).filter((item) => matchesSearch(item, search))
  const groups = groupByParty(matching)

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <h1 className="text-2xl font-bold tracking-tight">Waiting On</h1>
      <ListFilters
        count={overview.data ? resultCount(matching.length, 'item') : undefined}
        active={team === 'all' ? 0 : 1}
        onClear={() => void setFilters({ team: null })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <SearchInput
              placeholder="Search supplier, PO or item..."
              aria-label="Search what we're waiting on"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Team">
          <Select value={team} onValueChange={(value) => void setFilters({ team: value as (typeof TEAMS)[number] })}>
            <SelectTrigger aria-label="Team" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAMS.map((value) => (
                <SelectItem key={value} value={value}>
                  {TEAM_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
      </ListFilters>

      {overview.isError ? (
        <p className="text-sm text-muted-foreground">{"Couldn't load what we're waiting on. Refresh to try again."}</p>
      ) : overview.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">{search ? 'Nothing matches that search.' : 'Nothing owed.'}</p>
      ) : (
        <Card className="gap-0 py-0">
          <CardContent className="divide-y p-0">
            {groups.map((group) => (
              <section key={group.party} className="space-y-2 px-4 py-3">
                <h2 className="text-sm font-semibold">{group.party}</h2>
                <ul className="divide-y" aria-label={`Waiting on ${group.party}`}>
                  {group.items.map((item) => (
                    <OverviewRow key={item.uuid} item={item} />
                  ))}
                </ul>
              </section>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
